import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaFileInvoiceDollar,
  FaHandshake,
  FaSearch,
  FaTimes,
  FaWallet
} from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './RentalArchivePage.scss';

const INVOICED_DRAFT_STORAGE_KEY = 'accountingInvoicedDrafts';
const RENTAL_TRACKING_CACHE_KEY = 'rentalTrackingCacheV3';
const PAGE_SIZE = 13;
const DEFAULT_COMMISSION_RATE = 10;

const readJsonStorage = (storage, key, fallback) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.value)) return payload.value;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const firstValue = (...values) => {
  const found = values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
  return found === undefined ? '' : found;
};

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value === undefined || value === null || value === '') return 0;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(toNumber(value));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('tr-TR');
};

const formatRange = (start, end) => `${formatDate(start)} - ${formatDate(end)}`;

const normalizeText = (value) => String(value || '').toLocaleLowerCase('tr-TR');

const addMonths = (value, monthOffset) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setMonth(date.getMonth() + monthOffset);
  return date;
};

const getRentalId = (rental) => firstValue(rental?.id, rental?.Id, rental?.rentalId, rental?.RentalId);
const getCustomerName = (rental) =>
  firstValue(rental?.rentingCompany, rental?.customerCompanyName, rental?.companyName, rental?.tenantCompanyName, rental?.renterName, rental?.customerTitle, '-');
const getOwnerName = (rental) =>
  firstValue(rental?.owner, rental?.ownerCompanyName, rental?.towerCraneOwner, rental?.towerCraneOwnerName, rental?.ownerName, rental?.craneOwnerName, '-');
const getWorkSite = (rental) => firstValue(rental?.workSite, rental?.siteName, rental?.constructionSite, rental?.constructionSiteName, rental?.projectName, '-');
const getLocation = (rental) => firstValue(rental?.constructionSiteLocation, rental?.location, rental?.siteAddress, rental?.address, '-');
const getCraneDisplay = (rental) => {
  const serial = firstValue(rental?.craneSerialNumber, rental?.towerCraneSerialNumber, rental?.serialNumber, rental?.towerCrane);
  const model = firstValue(rental?.towerCrane, rental?.model, rental?.craneModel);
  if (serial && model && serial !== model) return `${serial} - ${model}`;
  return firstValue(serial, model, '-');
};
const getMonthlyAmount = (rental) =>
  toNumber(firstValue(rental?.rent, rental?.monthlyRent, rental?.monthlyRentalPrice, rental?.monthlyAmount));
const getInstallmentCount = (rental) =>
  Math.max(toNumber(firstValue(rental?.invoiceInstallmentCount, rental?.rentalPeriod, rental?.monthCount, rental?.durationMonths)), 0);
const getStartDate = (rental) => firstValue(rental?.rentalStartDate, rental?.startDate, rental?.beginDate);
const getEndDate = (rental) => firstValue(rental?.rentalEndDate, rental?.endDate, rental?.finishDate);

const getOneTimeItems = (rental) =>
  (rental?.lineItems || rental?.LineItems || rental?.serviceItems || []).filter((item) => {
    const itemType = String(firstValue(item.itemType, item.ItemType, item.type, item.Type)).toLocaleLowerCase('tr-TR');
    return itemType === '1' || itemType === 'onetime' || itemType === 'one-time';
  });

const getLineItemTotal = (item) => {
  const directAmount = toNumber(firstValue(item.amount, item.Amount, item.total, item.Total));
  if (directAmount > 0) return directAmount;
  return toNumber(firstValue(item.quantity, item.Quantity, 1)) * toNumber(firstValue(item.unitPrice, item.UnitPrice, item.price, item.Price));
};

const getLineItemLabel = (item) =>
  firstValue(item.description, item.Description, item.name, item.Name, item.serviceName, item.ServiceName, 'Hizmet Kalemi');

const getCommissionRate = (rental) => {
  const explicitRate = toNumber(firstValue(rental?.commissionRate, rental?.CommissionRate));
  return explicitRate > 0 ? explicitRate : DEFAULT_COMMISSION_RATE;
};

const getInvoiceRecordsForRental = (rental, invoicedDrafts) => {
  const rentalId = Number(getRentalId(rental));
  const embeddedRecords = normalizeApiList(firstValue(rental?.invoiceRecords, rental?.invoices, rental?.issuedInvoices, []));
  const localRecords = Object.values(invoicedDrafts || {}).filter((record) => {
    if (record?.type !== 'rental') return false;
    const rentalIds = Array.isArray(record.rentalIds) ? record.rentalIds.map(Number) : [];
    if (rentalIds.includes(rentalId)) return true;
    if (Number(record.sourceId) === rentalId) return true;

    const draftId = String(record.draftId || '');
    const matched = draftId.match(/^rental-[0-9a-f-]{36}-(.+)$/i);
    const parsedIds = matched?.[1]
      ? matched[1].split('-').map(Number).filter((id) => Number.isFinite(id))
      : [];
    return parsedIds.includes(rentalId);
  });

  return [...embeddedRecords, ...localRecords];
};

const buildInvoicePlan = (rental, invoiceRecords) => {
  const installments = getInstallmentCount(rental);
  const monthlyAmount = getMonthlyAmount(rental);
  const firstInvoiceDate = firstValue(rental?.firstInvoiceDate, getStartDate(rental));
  const oneTimeItems = getOneTimeItems(rental);
  const oneTimeTotal = oneTimeItems.reduce((sum, item) => sum + getLineItemTotal(item), 0);
  const rentalId = Number(getRentalId(rental));
  const invoicedInstallments = new Set();
  const invoicedOneTimeItems = new Set();

  invoiceRecords.forEach((record) => {
    (record.rentalInstallments || record.installments || []).forEach((item) => {
      if (Number(firstValue(item.rentalId, item.RentalId, rentalId)) === rentalId) {
        invoicedInstallments.add(Number(firstValue(item.installmentNo, item.InstallmentNo, item.monthNo)));
      }
    });
    (record.rentalOneTimeItemIds || record.oneTimeItemIds || []).forEach((id) => invoicedOneTimeItems.add(Number(id)));
  });

  const monthlyPlan = Array.from({ length: installments }, (_, index) => {
    const installmentNo = index + 1;
    const oneTimeItemsInvoiced = oneTimeItems.every((item, itemIndex) => {
      const itemId = Number(firstValue(item.id, item.Id, itemIndex + 1));
      return invoicedOneTimeItems.has(itemId);
    });

    return {
      key: `month-${installmentNo}`,
      label: `${installmentNo}. Ay Kira Bedeli`,
      date: addMonths(firstInvoiceDate, index),
      amount: installmentNo === 1 ? monthlyAmount + oneTimeTotal : monthlyAmount,
      isInvoiced: invoicedInstallments.has(installmentNo) || (installmentNo === 1 && oneTimeItems.length > 0 && oneTimeItemsInvoiced),
      firstInvoiceItems: installmentNo === 1 ? oneTimeItems : []
    };
  });

  return monthlyPlan;
};

const getEarlyCloseInfo = (rental) => {
  const plannedEnd = getEndDate(rental) ? new Date(getEndDate(rental)) : null;
  const actualEndValue = firstValue(rental?.actualEndDate, rental?.earlyCloseDate, rental?.closedEarlyAt, rental?.manualCloseDate);
  const actualEnd = actualEndValue ? new Date(actualEndValue) : null;
  const isDateEarly =
    plannedEnd &&
    actualEnd &&
    !Number.isNaN(plannedEnd.getTime()) &&
    !Number.isNaN(actualEnd.getTime()) &&
    actualEnd < plannedEnd;

  return {
    isEarlyClosed: Boolean(firstValue(rental?.isEarlyClosed, rental?.earlyClosed, false)) || isDateEarly,
    date: actualEndValue,
    reason: firstValue(rental?.earlyCloseReason, rental?.closeReason, rental?.closureNote, '')
  };
};

const buildRentalSummary = (rental, invoicedDrafts) => {
  const invoiceRecords = getInvoiceRecordsForRental(rental, invoicedDrafts);
  const invoicePlan = buildInvoicePlan(rental, invoiceRecords);
  const monthlyTotal = getMonthlyAmount(rental) * getInstallmentCount(rental);
  const serviceTotal = getOneTimeItems(rental).reduce((sum, item) => sum + getLineItemTotal(item), 0);
  const totalAmount = toNumber(firstValue(rental?.totalAmount, rental?.contractTotal, rental?.totalPrice)) || monthlyTotal + serviceTotal;
  const commissionRate = getCommissionRate(rental);
  const rentalBaseAmount = monthlyTotal || Math.max(totalAmount - serviceTotal, 0);
  const rentalCommissionAmount = rentalBaseAmount * (commissionRate / 100);
  const profitAmount = rentalCommissionAmount + serviceTotal;
  const ownerPayable = Math.max(rentalBaseAmount - rentalCommissionAmount, 0);
  const invoicedCount = invoicePlan.filter((item) => item.isInvoiced).length;
  const pendingCount = Math.max(invoicePlan.length - invoicedCount, 0);
  const endDate = getEndDate(rental) ? new Date(getEndDate(rental)) : null;
  const daysToEnd = endDate && !Number.isNaN(endDate.getTime())
    ? Math.ceil((endDate.setHours(23, 59, 59, 999) - Date.now()) / 86400000)
    : null;
  const earlyClose = getEarlyCloseInfo(rental);

  return {
    invoiceRecords,
    invoicePlan,
    monthlyTotal,
    serviceTotal,
    totalAmount,
    commissionRate,
    rentalBaseAmount,
    rentalCommissionAmount,
    profitAmount,
    commissionAmount: profitAmount,
    ownerPayable,
    invoicedCount,
    pendingCount,
    invoiceCount: invoicePlan.length,
    daysToEnd,
    earlyClose,
    isCompleted: invoicePlan.length > 0 && pendingCount === 0,
    isEndingSoon: daysToEnd !== null && daysToEnd >= 0 && daysToEnd <= 30,
    serviceLineCount: getOneTimeItems(rental).length
  };
};

const getStatusLabel = (rental) => {
  if (rental.archive.earlyClose.isEarlyClosed) return 'Erken Kapatıldı';
  if (rental.archive.isCompleted) return 'Fatura Planı Tamamlandı';
  if (rental.archive.isEndingSoon) return 'Bitişi Yaklaşıyor';
  if (rental.archive.pendingCount > 0) return 'Fatura Bekliyor';
  return 'Takipte';
};

const getStatusClass = (rental) => {
  if (rental.archive.earlyClose.isEarlyClosed) return 'early';
  if (rental.archive.isCompleted) return 'completed';
  if (rental.archive.isEndingSoon) return 'ending';
  if (rental.archive.pendingCount > 0) return 'pending';
  return 'active';
};

const getPaymentStatus = (rental) => {
  if (rental.archive.invoiceCount === 0) return 'Fatura planı yok';
  if (rental.archive.pendingCount === 0) return 'Tamamlandı';
  return `${rental.archive.pendingCount} fatura bekliyor`;
};

const shortText = (value, length = 48) => {
  const text = String(value || '-');
  return text.length > length ? `${text.slice(0, length)}...` : text;
};

const RentalArchivePage = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRentalId, setSelectedRentalId] = useState(null);
  const [detailRental, setDetailRental] = useState(null);
  const [invoicedDrafts, setInvoicedDrafts] = useState({});
  const [activeTab, setActiveTab] = useState('active');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', owner: '', status: '', dateFrom: '', dateTo: '' });

  const fetchRentals = async ({ preferCache = false } = {}) => {
    const cached = readJsonStorage(sessionStorage, RENTAL_TRACKING_CACHE_KEY, null);
    if (preferCache && cached?.length) {
      setRentals(cached);
      setLoading(false);
    }

    try {
      if (!preferCache) setLoading(true);
      const attempts = [
        { params: { includeLineItems: true } },
        { params: { serviceStatus: 'Approved', includeLineItems: true } },
        {}
      ];
      let payload = [];

      for (const options of attempts) {
        const response = await api.get('/Rental', options);
        payload = normalizeApiList(response.data);
        if (payload.length > 0) break;
      }

      setRentals(payload);
      sessionStorage.setItem(RENTAL_TRACKING_CACHE_KEY, JSON.stringify(payload));
      setError('');
    } catch {
      if (!cached?.length) setError('Kiralama kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInvoicedDrafts(readJsonStorage(localStorage, INVOICED_DRAFT_STORAGE_KEY, {}));
    fetchRentals({ preferCache: true });
  }, []);

  const enrichedRentals = useMemo(
    () => rentals.map((rental) => ({ ...rental, archive: buildRentalSummary(rental, invoicedDrafts) })),
    [rentals, invoicedDrafts]
  );

  const owners = useMemo(
    () => [...new Set(enrichedRentals.map(getOwnerName).filter((owner) => owner && owner !== '-'))].sort((a, b) => a.localeCompare(b, 'tr')),
    [enrichedRentals]
  );

  const filteredRentals = useMemo(() => {
    const search = normalizeText(filters.search);
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;

    return enrichedRentals
      .filter((rental) => {
        const text = normalizeText([
          getCraneDisplay(rental),
          getCustomerName(rental),
          getOwnerName(rental),
          getWorkSite(rental),
          getLocation(rental),
          firstValue(rental.type, rental.rentalType)
        ].join(' '));
        if (search && !text.includes(search)) return false;
        if (filters.owner && getOwnerName(rental) !== filters.owner) return false;
        if (filters.status && getStatusClass(rental) !== filters.status) return false;

        const startDate = getStartDate(rental) ? new Date(getStartDate(rental)) : null;
        if (from && startDate && startDate < from) return false;
        if (to && startDate && startDate > to) return false;

        if (activeTab === 'active') return !rental.archive.isCompleted && !rental.archive.earlyClose.isEarlyClosed;
        if (activeTab === 'completed') return rental.archive.isCompleted;
        if (activeTab === 'early') return rental.archive.earlyClose.isEarlyClosed;
        return true;
      })
      .sort((a, b) => new Date(getStartDate(b) || 0) - new Date(getStartDate(a) || 0));
  }, [activeTab, enrichedRentals, filters]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, filters.search, filters.owner, filters.status, filters.dateFrom, filters.dateTo]);

  const totals = useMemo(() => {
    const activeCount = enrichedRentals.filter((rental) => !rental.archive.isCompleted && !rental.archive.earlyClose.isEarlyClosed).length;
    const completedCount = enrichedRentals.filter((rental) => rental.archive.isCompleted).length;
    const earlyClosedCount = enrichedRentals.filter((rental) => rental.archive.earlyClose.isEarlyClosed).length;
    const endingSoonCount = enrichedRentals.filter((rental) => rental.archive.isEndingSoon).length;
    const pendingInvoiceCount = enrichedRentals.reduce((sum, rental) => sum + rental.archive.pendingCount, 0);
    const totalVolume = enrichedRentals.reduce((sum, rental) => sum + rental.archive.totalAmount, 0);
    const totalRentalCommission = enrichedRentals.reduce((sum, rental) => sum + rental.archive.rentalCommissionAmount, 0);
    const totalProfit = enrichedRentals.reduce((sum, rental) => sum + rental.archive.profitAmount, 0);
    const totalCommission = totalProfit;
    const ownerPayable = enrichedRentals.reduce((sum, rental) => sum + rental.archive.ownerPayable, 0);
    return { activeCount, completedCount, earlyClosedCount, endingSoonCount, pendingInvoiceCount, totalVolume, totalRentalCommission, totalProfit, totalCommission, ownerPayable };
  }, [enrichedRentals]);

  const ownerTotals = useMemo(() => {
    const grouped = enrichedRentals.reduce((acc, rental) => {
      const owner = getOwnerName(rental) || 'Vinç sahibi belirtilmedi';
      if (!acc[owner]) acc[owner] = { owner, payable: 0, volume: 0, commission: 0, count: 0 };
      acc[owner].payable += rental.archive.ownerPayable;
      acc[owner].volume += rental.archive.totalAmount;
      acc[owner].commission += rental.archive.commissionAmount;
      acc[owner].count += 1;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.payable - a.payable);
  }, [enrichedRentals]);

  const displayedOwnerTotals = useMemo(() => {
    const visibleOwners = ownerTotals.slice(0, 5);
    const remainingOwners = ownerTotals.slice(5);
    if (remainingOwners.length === 0) return visibleOwners;

    const otherOwners = remainingOwners.reduce(
      (acc, item) => ({
        owner: 'Diğer vinç sahipleri',
        payable: acc.payable + item.payable,
        volume: acc.volume + item.volume,
        commission: acc.commission + item.commission,
        count: acc.count + item.count
      }),
      { owner: 'Diğer vinç sahipleri', payable: 0, volume: 0, commission: 0, count: 0 }
    );

    return [...visibleOwners, otherOwners];
  }, [ownerTotals]);

  const totalPages = Math.max(Math.ceil(filteredRentals.length / PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);
  const pagedRentals = filteredRentals.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const selectedRental = useMemo(
    () => filteredRentals.find((rental) => Number(getRentalId(rental)) === Number(selectedRentalId)) || filteredRentals[0] || null,
    [filteredRentals, selectedRentalId]
  );

  const maxOwnerPayable = Math.max(...displayedOwnerTotals.map((item) => item.payable), 1);
  const profitShare = totals.totalVolume ? Math.round((totals.totalProfit / totals.totalVolume) * 100) : 0;

  const clearFilters = () => {
    setFilters({ search: '', owner: '', status: '', dateFrom: '', dateTo: '' });
    setActiveTab('active');
  };

  return (
    <div className="rental-tracking-page">
      <Navbar />
      <main className="rental-tracking-shell">
        <section className="rental-page-head">
          <div>
            <span>Kiralama Takip</span>
            <h1>Kiralama Takip Paneli</h1>
            <p>Kiracı firma, vinç sahibi payı, kazanç, fatura planı ve erken kapatma kayıtlarını tek ekrandan takip edin.</p>
          </div>
          <div className="rental-profit-card">
            <small>Toplam Kazanç</small>
            <strong>{formatCurrency(totals.totalProfit)}</strong>
            <em>Kira komisyonu ve ek hizmet gelirleri</em>
          </div>
        </section>

        <section className="rental-kpi-grid">
          <div className="rental-kpi-card"><FaHandshake /><span>Aktif Kiralama</span><strong>{totals.activeCount}</strong></div>
          <div className="rental-kpi-card"><FaClock /><span>Bitişi Yaklaşan</span><strong>{totals.endingSoonCount}</strong></div>
          <div className="rental-kpi-card"><FaFileInvoiceDollar /><span>Fatura Bekleyen</span><strong>{totals.pendingInvoiceCount}</strong></div>
          <div className="rental-kpi-card"><FaCheckCircle /><span>Tamamlanan</span><strong>{totals.completedCount}</strong></div>
          <div className="rental-kpi-card wide"><FaChartLine /><span>Toplam Sözleşme</span><strong>{formatCurrency(totals.totalVolume)}</strong></div>
          <div className="rental-kpi-card wide"><FaWallet /><span>Mal Sahibi Payı</span><strong>{formatCurrency(totals.ownerPayable)}</strong></div>
        </section>

        <section className="rental-command-card">
          <div className="rental-tabs" role="tablist" aria-label="Kiralama durum filtreleri">
            {[
              ['active', 'Aktif', totals.activeCount],
              ['completed', 'Tamamlanan', totals.completedCount],
              ['early', 'Erken Kapatılan', totals.earlyClosedCount],
              ['all', 'Tümü', enrichedRentals.length]
            ].map(([key, label, count]) => (
              <button key={key} type="button" className={activeTab === key ? 'active' : ''} onClick={() => setActiveTab(key)}>
                {label} <span>{count}</span>
              </button>
            ))}
          </div>
          <button type="button" className="refresh-button" onClick={() => fetchRentals()}>Yenile</button>
        </section>

        <section className="rental-filter-panel">
          <label className="search-field">
            <FaSearch />
            <input
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="Vinç, kiracı firma, şantiye veya seri no ara"
            />
          </label>
          <select value={filters.owner} onChange={(event) => setFilters((prev) => ({ ...prev, owner: event.target.value }))}>
            <option value="">Tüm Vinç Sahipleri</option>
            {owners.map((owner) => <option value={owner} key={owner}>{owner}</option>)}
          </select>
          <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}>
            <option value="">Tüm Durumlar</option>
            <option value="pending">Fatura Bekliyor</option>
            <option value="ending">Bitişi Yaklaşıyor</option>
            <option value="completed">Fatura Planı Tamamlandı</option>
            <option value="early">Erken Kapatıldı</option>
          </select>
          <input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} />
          <input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} />
          <button type="button" className="clear-filter-button" onClick={clearFilters}>Temizle</button>
        </section>

        {error && <div className="rental-error">{error}</div>}

        <section className="rental-tracking-layout">
          <div className="rental-table-card">
            <div className="section-title-row">
              <div>
                <h2>Kiralama Kayıtları</h2>
                <p>{filteredRentals.length} kayıt listeleniyor. Detay için satıra tıklayın.</p>
              </div>
            </div>

            {loading ? (
              <div className="rental-empty-state">Kiralama kayıtları yükleniyor...</div>
            ) : pagedRentals.length === 0 ? (
              <div className="rental-empty-state">
                <strong>Filtreye uygun kiralama kaydı bulunamadı.</strong>
                <span>Aktif/tamamlanan sekmelerini ve tarih filtrelerini kontrol edin.</span>
              </div>
            ) : (
              <>
                <div className="rental-table-head">
                  <span>Vinç / Kiracı</span>
                  <span>Vinç Sahibi</span>
                  <span>Dönem</span>
                  <span>Kazanç</span>
                  <span>Mal Sahibi Payı</span>
                  <span>Durum</span>
                </div>
                <div className="rental-table-body">
                  {pagedRentals.map((rental) => (
                    <button
                      type="button"
                      key={getRentalId(rental)}
                      className={`rental-table-row ${Number(getRentalId(selectedRental)) === Number(getRentalId(rental)) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedRentalId(getRentalId(rental));
                        setDetailRental(rental);
                      }}
                    >
                      <span><strong>{getCraneDisplay(rental)}</strong><small>{shortText(getCustomerName(rental), 42)}</small></span>
                      <span>{shortText(getOwnerName(rental), 42)}</span>
                      <span><strong>{formatDate(getStartDate(rental))}</strong><small>{formatDate(getEndDate(rental))}</small></span>
                      <span><strong>{formatCurrency(rental.archive.profitAmount)}</strong><small>Kira + hizmet</small></span>
                      <span>{formatCurrency(rental.archive.ownerPayable)}</span>
                      <span className={`rental-status ${getStatusClass(rental)}`}>{getStatusLabel(rental)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="rental-pagination">
              <button type="button" onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} aria-label="Önceki sayfa"><FaArrowLeft /></button>
              <span>Sayfa {currentPage} / {totalPages}</span>
              <button type="button" onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} aria-label="Sonraki sayfa"><FaArrowRight /></button>
            </div>
          </div>

          <aside className="rental-side-panel">
            <div className="rental-side-card finance-card">
              <h3>Finans Özeti</h3>
              <div className="revenue-line">
                <span>Toplam Kiralama</span>
                <strong>{formatCurrency(totals.totalVolume)}</strong>
              </div>
              <div className="split-bar" aria-hidden="true">
                <i style={{ width: `${Math.min(Math.max(profitShare, 0), 100)}%` }} />
              </div>
              <div className="finance-breakdown">
                <div><span>Toplam Kazanç</span><strong>{formatCurrency(totals.totalProfit)}</strong></div>
                <div><span>Kira Komisyonu</span><strong>{formatCurrency(totals.totalRentalCommission)}</strong></div>
                <div><span>Vinç Sahibi Payı</span><strong>{formatCurrency(totals.ownerPayable)}</strong></div>
                <div><span>Bekleyen Fatura</span><strong>{totals.pendingInvoiceCount}</strong></div>
              </div>
            </div>

            <div className="rental-side-card owner-payables-card">
              <h3>Vinç Sahibi Ödemeleri</h3>
              <div className="owner-payables-list">
                {displayedOwnerTotals.length === 0 ? <p className="muted">Ödeme verisi yok.</p> : displayedOwnerTotals.map((item) => (
                  <div className="owner-payable-row" key={item.owner}>
                    <div><strong>{shortText(item.owner, 30)}</strong><span>{item.count} kiralama</span></div>
                    <b>{formatCurrency(item.payable)}</b>
                    <i style={{ width: `${Math.max((item.payable / maxOwnerPayable) * 100, 6)}%` }} />
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>
      </main>

      {detailRental && (
        <div className="rental-detail-backdrop" role="dialog" aria-modal="true">
          <div className="rental-detail-modal">
            <header>
              <div>
                <span>{getCraneDisplay(detailRental)}</span>
                <h2>{getCustomerName(detailRental)}</h2>
                <p>{formatRange(getStartDate(detailRental), getEndDate(detailRental))}</p>
              </div>
              <button type="button" onClick={() => setDetailRental(null)} aria-label="Kapat"><FaTimes /></button>
            </header>

            <div className="rental-detail-grid">
              <div><span>Kiracı Firma</span><strong>{getCustomerName(detailRental)}</strong></div>
              <div><span>Vinç Sahibi</span><strong>{getOwnerName(detailRental)}</strong></div>
              <div><span>Şantiye</span><strong>{getWorkSite(detailRental)}</strong></div>
              <div><span>Konum</span><strong>{getLocation(detailRental)}</strong></div>
              <div><span>Kira Dönemi</span><strong>{formatRange(getStartDate(detailRental), getEndDate(detailRental))}</strong></div>
              <div><span>Ödeme Durumu</span><strong>{getPaymentStatus(detailRental)}</strong></div>
              <div><span>Fatura Planı</span><strong>{detailRental.archive.invoicedCount}/{detailRental.archive.invoiceCount}</strong></div>
              <div className="highlight"><span>Toplam Kazanç</span><strong>{formatCurrency(detailRental.archive.profitAmount)}</strong></div>
              <div><span>Toplam Sözleşme</span><strong>{formatCurrency(detailRental.archive.totalAmount)}</strong></div>
              <div><span>Vinç Sahibine Aktarılacak</span><strong>{formatCurrency(detailRental.archive.ownerPayable)}</strong></div>
              <div><span>Ek Hizmet</span><strong>{detailRental.archive.serviceLineCount} kalem</strong></div>
              <div><span>Durum</span><strong>{getStatusLabel(detailRental)}</strong></div>
            </div>

            <div className="rental-detail-columns">
              <section className="rental-detail-section">
                <div className="detail-section-head"><h3>Fatura Planı</h3><span>{detailRental.archive.pendingCount} beklemede</span></div>
                <div className="invoice-plan-list">
                  {detailRental.archive.invoicePlan.length > 0 ? detailRental.archive.invoicePlan.map((item) => (
                    <div className="invoice-plan-row" key={item.key}>
                      <div>
                        <strong>{item.label}</strong>
                        <span>{formatDate(item.date)}</span>
                        {item.firstInvoiceItems?.length > 0 && (
                          <div className="first-invoice-preview">
                            <small>İlk faturaya dahil</small>
                            {item.firstInvoiceItems.map((serviceItem, serviceIndex) => (
                              <small key={`${getLineItemLabel(serviceItem)}-${serviceIndex}`}>
                                {getLineItemLabel(serviceItem)} · {formatCurrency(getLineItemTotal(serviceItem))}
                              </small>
                            ))}
                          </div>
                        )}
                      </div>
                      <b>{formatCurrency(item.amount)}</b>
                      <em className={item.isInvoiced ? 'paid' : ''}>{item.isInvoiced ? 'Kesildi' : 'Beklemede'}</em>
                    </div>
                  )) : <p>Bu kiralama için fatura planı bulunamadı.</p>}
                </div>
              </section>

              <section className="rental-detail-section">
                <div className="detail-section-head"><h3>Finans Özeti</h3><span>%{detailRental.archive.commissionRate} komisyon</span></div>
                <div className="finance-breakdown large">
                  <div><span>Aylık Kira Toplamı</span><strong>{formatCurrency(detailRental.archive.monthlyTotal)}</strong></div>
                  <div><span>Ek Hizmet Toplamı</span><strong>{formatCurrency(detailRental.archive.serviceTotal)}</strong></div>
                  <div><span>Kira Komisyonu</span><strong>{formatCurrency(detailRental.archive.rentalCommissionAmount)}</strong></div>
                  <div><span>Star Crane Kazancı</span><strong>{formatCurrency(detailRental.archive.profitAmount)}</strong></div>
                  <div><span>Mal Sahibi Payı</span><strong>{formatCurrency(detailRental.archive.ownerPayable)}</strong></div>
                </div>
                <div className="rental-service-lines">
                  <h4>Hizmet Kalemleri</h4>
                  {getOneTimeItems(detailRental).length > 0 ? getOneTimeItems(detailRental).map((item, index) => (
                    <div key={`${getLineItemLabel(item)}-${index}`}><span>{getLineItemLabel(item)}</span><strong>{formatCurrency(getLineItemTotal(item))}</strong></div>
                  )) : <p>Ek hizmet kalemi bulunmuyor.</p>}
                </div>
              </section>
            </div>

            <section className="rental-detail-section early-close-section">
              <div className="detail-section-head"><h3>Erken Kapatma Geçmişi</h3></div>
              {detailRental.archive.earlyClose.isEarlyClosed ? (
                <div className="early-close-row">
                  <strong>{formatDate(detailRental.archive.earlyClose.date)}</strong>
                  <span>{detailRental.archive.earlyClose.reason || 'Erken kapatma notu girilmemiş.'}</span>
                </div>
              ) : <p className="muted">Bu kiralama için erken kapatma kaydı yok.</p>}
            </section>

            <section className="rental-detail-section">
              <div className="detail-section-head"><h3>Kesilen Faturalar</h3><span>{detailRental.archive.invoiceRecords.length} kayıt</span></div>
              {detailRental.archive.invoiceRecords.length > 0 ? (
                <div className="issued-invoice-list">
                  {detailRental.archive.invoiceRecords.map((record, index) => (
                    <div key={firstValue(record.draftId, record.invoiceNumber, record.sentAt, index)}>
                      <strong>{firstValue(record.invoiceNumber, record.documentNumber, 'Belge no yok')}</strong>
                      <span>{formatDate(firstValue(record.sentAt, record.date, record.createdAt))}</span>
                      <small>{firstValue(record.ettn, record.uuid, '-')}</small>
                    </div>
                  ))}
                </div>
              ) : <p className="muted">Bu kiralama için kesilmiş fatura kaydı yok.</p>}
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalArchivePage;

